import { clsx } from "clsx";
import { type ReactElement, useState } from "react";
import { ActionButton, Badge, Description, FormError, HStack, Label, Spacer, VStack } from "widgets";
import formStyle from "widgets/form.module.css";
import { useDataAttributes } from "widgets/hooks";

import type { GenomeConstraints } from "@/graphql.ts";
import { GenomeSelectionModal } from "@/modules/workflow/components/parameters/GenomeSelectionModal.tsx";
import { SelectedGenomesTiles } from "@/modules/workflow/components/parameters/SelectedGenomesTiles.tsx";
import { useGetOrganismsQuery } from "@/modules/workflow/graphql/coordinate-lists.gql.ts";

interface GenomeSelectionWidgetProps {
  label: string;
  description?: string | null;
  isRequired?: boolean;
  isDisabled?: boolean;
  errorMessage?: string;
  extraAction?: ReactElement;
  selectedOrganisms: number[] | null;
  onSelectionChange: (selectedGenomes: number[]) => void;
  multiSelection?: boolean;
  genomeConstraints?: GenomeConstraints;
}

export const GenomeSelectionWidget = ({
  label,
  description,
  isRequired,
  isDisabled,
  errorMessage,
  onSelectionChange,
  selectedOrganisms,
  multiSelection,
  genomeConstraints,
  ...props
}: GenomeSelectionWidgetProps) => {
  const [open, setOpen] = useState(false);
  const dataAttributes = useDataAttributes(props);

  const noGenomesAvailable = selectedOrganisms?.length === 0;

  const { data, loading: isLoadingOrganisms } = useGetOrganismsQuery({
    variables: { orgIds: selectedOrganisms ?? [] },
    skip: selectedOrganisms?.length === 0,
  });

  return (
    <>
      <VStack
        className={formStyle.input_layout}
        data-testid={`${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}multi-select__button`}
      >
        {!!label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label>{label}</Label>
            <Spacer />
            {!isRequired && <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />}
          </HStack>
        )}
        <Description
          data-testid="genome-selection-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: label != undefined && !isRequired,
          })}
        >
          {description}
        </Description>
        <div className={formStyle.input_layout_column1}>
          <SelectedGenomesTiles
            organisms={data?.organisms ?? []}
            isLoading={isLoadingOrganisms}
            onClearSelection={(genomeId) => {
              const newSelection = selectedOrganisms?.filter((g) => g !== genomeId);
              if (newSelection) {
                onSelectionChange(newSelection);
              }
            }}
            disabled={isDisabled ?? false}
          />
        </div>
        {(errorMessage || noGenomesAvailable) && (
          <FormError data-testid={"genome-selection-field-error"}>
            {errorMessage ? errorMessage : noGenomesAvailable ? "Please select a genome first" : null}
          </FormError>
        )}
        <HStack className={formStyle.input_layout_column1} style={{ width: "100%", paddingTop: "3px" }} wrap>
          <ActionButton
            label={multiSelection ? "Select Genomes" : "Select Genome"}
            onPress={() => setOpen(true)}
            style={{ flex: "1 0 auto" }}
            isDisabled={isDisabled}
          />
        </HStack>
        {!!props.extraAction && <HStack className={formStyle.input_layout_column2}>{props.extraAction}</HStack>}
      </VStack>
      {/* Force unmount for proper reset and table setup */}
      {open && (
        <GenomeSelectionModal
          isOpen={open}
          onClose={() => setOpen(false)}
          onSave={onSelectionChange}
          initialSelection={selectedOrganisms ?? []}
          multiSelection={multiSelection}
          genomeConstraints={genomeConstraints}
        />
      )}
    </>
  );
};
